import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LandingPage.css';

/* ============================================
   Sub-components
   ============================================ */

const LandingNav: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <nav className="lp-nav">
      <div className="lp-nav-brand">
        <div className="lp-nav-logo">W</div>
        <span className="lp-nav-title">Wealth Financial Systems</span>
      </div>
      <div className="lp-nav-actions">
        {isAuthenticated ? (
          <Link to="/dashboard" className="lp-btn lp-btn-primary">Go to Dashboard</Link>
        ) : (
          <Link to="/login" className="lp-btn lp-btn-primary">Get Started</Link>
        )}
      </div>
    </nav>
  );
};

/* ---- Stats Strip ---- */

const StatsStrip: React.FC = () => (
  <div className="lp-stats-strip">
    <div className="lp-stats-inner">
      {[
        { val: '$0',   label: 'To get started' },
        { val: '5',    label: 'Stages to freedom' },
        { val: '4',    label: 'Research models' },
        { val: 'Swing', label: 'Trade focused' },
      ].map(s => (
        <div key={s.label} className="lp-stat">
          <div className="lp-stat-val">{s.val}</div>
          <div className="lp-stat-label">{s.label}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ---- Hero ---- */

const HeroSection: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return (
    <section className="lp-hero">
      <h1>WEALTH FINANCIAL SYSTEM</h1>
      <p className="lp-hero-subtitle">
        The tools, strategy, and market intelligence to grow wealth —
        whether you're learning to invest, actively trading, or building a business.
      </p>
      <div className="lp-hero-actions">
        <Link to="/login" className="lp-btn lp-btn-primary lp-btn-lg">
          {isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
        </Link>
      </div>
    </section>
  );
};

/* ---- Audience Segments ---- */

const AudienceSection: React.FC = () => {
  const segments = [
    {
      label: 'LEARN',
      title: 'I Want to Build Wealth',
      desc: 'Free tools to plan your financial future — net worth tracking, FIRE calculator, lifecycle planner, and the Wealth Playbook.',
      color: '#10b981',
      link: '/wealth-building',
      cta: 'Start Learning',
      icon: 'WLT',
    },
    {
      label: 'TRADE',
      title: 'I\'m an Individual Trader',
      desc: 'Professional trading research for SPX, SPY, ES, MES, stocks, and automated algo engines — powered by live market data and AI.',
      color: '#8b5cf6',
      link: '/trading',
      cta: 'See Research Products',
      icon: 'TRD',
    },
    {
      label: 'INFLUENCE',
      title: 'I\'m an Influencer',
      desc: 'White-label our platform under your brand. Offer our research to your audience and earn revenue on every subscriber.',
      color: '#ec4899',
      link: '/influencer',
      cta: 'Learn About Partnership',
      icon: 'INF',
    },
    {
      label: 'ENTERPRISE',
      title: 'I\'m an Enterprise',
      desc: 'Custom research models, dedicated infrastructure, API access, and white-glove support for funds and institutions.',
      color: '#3b82f6',
      link: '/enterprise',
      cta: 'Contact Sales',
      icon: 'ENT',
    },
  ];

  return (
    <section className="lp-section" style={{ maxWidth: 1000, margin: '0 auto' }}>
      <p className="lp-section-label">Who Are You?</p>
      <h2 className="lp-section-title" style={{ fontSize: '1.8rem' }}>Choose Your Path</h2>
      <p className="lp-section-desc" style={{ maxWidth: 550 }}>
        We serve four types of people. Pick the one that fits you best.
      </p>

      <div className="lp-audience-grid">
        {segments.map(s => (
          <Link to={s.link} key={s.label} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
            <div style={{
              background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.color}30`,
              borderRadius: 12, padding: '1.25rem', textAlign: 'center',
              transition: 'transform 0.2s, border-color 0.2s', cursor: 'pointer',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = `${s.color}`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = `${s.color}30`; }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: `${s.color}15`, border: `1px solid ${s.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '0.7rem', fontWeight: 800, color: s.color }}>{s.icon}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', color: s.color, marginBottom: '0.5rem' }}>{s.label}</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem', color: '#f59e0b' }}>{s.title}</h3>
              <p style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: 1.6 }}>{s.desc}</p>
              <div style={{ marginTop: '1rem', padding: '0.5rem 1rem', borderRadius: 8, background: `${s.color}15`, color: s.color, fontWeight: 600, fontSize: '0.85rem' }}>
                {s.cta} &rarr;
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

/* ---- Journey Section ---- */

interface Stage {
  key: string;
  name: string;
  title: string;
  description: string;
  tools: { name: string; route: string; desc: string; tier: 'free' | 'premier' | 'pro'; icon: string }[];
}

const STAGES: Stage[] = [
  {
    key: 'know',
    name: 'KNOW',
    title: 'Know Where You Stand',
    description: 'Before you build, you need to see the full picture. Track every asset, every liability, and understand your true financial position.',
    tools: [
      { name: 'Net Worth Tracker', route: '/net-worth',            desc: 'Track assets & liabilities',   tier: 'free', icon: '📊' },
      { name: 'Playbook',          route: '/playbook',             desc: 'Step-by-step wealth guide',    tier: 'free', icon: '📘' },
    ],
  },
  {
    key: 'plan',
    name: 'PLAN',
    title: 'Plan Your Path',
    description: 'Map out your investment lifecycle — from early accumulation through retirement. A plan turns intention into action.',
    tools: [
      { name: 'Lifecycle Planner', route: '/lifecycle-investment', desc: 'Age-based investment plan',    tier: 'free', icon: '🗺️' },
    ],
  },
  {
    key: 'build',
    name: 'BUILD',
    title: 'Build Your Freedom Number',
    description: 'Calculate exactly how much you need to never work again. Structure your retirement with the 4-Bucket System so you never sell in a crash.',
    tools: [
      { name: '4-Bucket Guide',          route: '/learn/4-bucket-system', desc: 'Learn the system first',        tier: 'free',    icon: '📚' },
      { name: 'Bucket Visualizer',        route: '/bucket-visualizer',     desc: '4-bucket allocation system',    tier: 'free',    icon: '🪣' },
      { name: 'FIRE Investment Strategy', route: '/fire-calculator',       desc: 'Financial independence planner',tier: 'premier', icon: '🔥' },
    ],
  },
  {
    key: 'monitor',
    name: 'MONITOR',
    title: 'Monitor the Markets',
    description: 'Stay ahead with real-time research, daily reports, and AI-driven market intelligence. This is where passive income meets active insight.',
    tools: [
      { name: 'Options Research',   route: '/options-research',   desc: 'Hourly IV-based reads',                tier: 'premier', icon: '📡' },
      { name: 'Stock Research',     route: '/stock-research',     desc: 'Technical + fundamental picks',        tier: 'premier', icon: '📈' },
    ],
  },
  {
    key: 'automate',
    name: 'AUTOMATE',
    title: 'Build Your Own Algo Policies',
    description: 'Design trading strategies as policies you control. Pick a strategy, set risk limits, and optionally enable AI to score each opportunity using technicals, sentiment, and fundamentals — all based on rules you define.',
    tools: [
      { name: 'Policy Engine',    route: '/algo/engines',    desc: 'Define strategies, risk limits & execution rules', tier: 'pro', icon: '🛠️' },
      { name: 'Signal Monitor',   route: '/algo/signals',    desc: 'See what your policies are detecting',            tier: 'pro', icon: '📡' },
      { name: 'Execution Log',    route: '/algo/trades',     desc: 'Track every trade your engine makes',             tier: 'pro', icon: '💹' },
      { name: 'Performance',      route: '/algo/portfolio',  desc: 'Measure how your policies perform',               tier: 'pro', icon: '📊' },
    ],
  },
];

const JourneySection: React.FC = () => {
  const [activeStage, setActiveStage] = useState(0);
  const stage = STAGES[activeStage];

  return (
    <section className="lp-section">
      <p className="lp-section-label">The Journey</p>
      <h2 className="lp-section-title">5 Stages to Financial Freedom</h2>
      <p className="lp-section-desc">
        Every tool in the system maps to a stage in your wealth-building journey.
      </p>

      <div className="lp-steps">
        {STAGES.map((s, i) => (
          <div className="lp-step-item" key={s.key}>
            <button
              className={`lp-step${activeStage === i ? ' active' : ''}`}
              onClick={() => setActiveStage(i)}
            >
              <span className="lp-step-num">{i + 1}</span>
              <span className="lp-step-name">{s.name}</span>
            </button>
            {i < STAGES.length - 1 && <span className="lp-step-arrow">›</span>}
          </div>
        ))}
      </div>

      <div className="lp-journey-card" key={stage.key}>
        <div className="lp-journey-header">
          <h3>{stage.title}</h3>
          <p>{stage.description}</p>
        </div>
        <div className="lp-journey-tools">
          {stage.tools.map((tool) => (
            <Link to={tool.route} className="lp-tool-card" key={tool.name + tool.route}>
              <div className={`lp-tool-icon ${tool.tier === 'free' ? 'lp-tool-icon-free' : tool.tier === 'pro' ? 'lp-tool-icon-pro' : 'lp-tool-icon-premium'}`}>
                {tool.icon}
              </div>
              <div className="lp-tool-info">
                <span className="lp-tool-name">
                  {tool.name}
                  {tool.tier === 'free' && <span className="lp-badge-free">Free</span>}
                  {tool.tier === 'premier' && <span className="lp-badge-premium">Premier</span>}
                  {tool.tier === 'pro' && <span className="lp-badge-pro">Pro</span>}
                </span>
                <span className="lp-tool-desc">{tool.desc}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---- Pricing ---- */

const PricingSection: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const signalProducts = [
    { name: 'SPX Notches', price: '$79', desc: '0DTE & multi-day credit spreads', color: '#8b5cf6', badge: 'MOST POPULAR', landingPage: '/spx' },
    { name: 'SPY Notches', price: '$49', desc: 'Equity options put-sell research', color: '#06b6d4', landingPage: '/spy' },
    { name: 'ES Notches', price: '$79', desc: 'E-mini futures options research', color: '#f59e0b', landingPage: '/es' },
    { name: 'MES Notches', price: '$49', desc: 'Micro E-mini research', color: '#10b981', landingPage: '/mes' },
  ];

  return (
    <section className="lp-section">
      <p className="lp-section-label">Pricing</p>
      <h2 className="lp-section-title">Pay Only for What You Trade</h2>
      <p className="lp-section-desc">
        Start free with wealth-building tools. Add research products individually, or bundle them all and save.
      </p>

      {/* Row 1: Free + Premier */}
      <div className="lp-pricing-grid lp-pricing-grid-3" style={{ marginBottom: '2rem' }}>
        {/* Free */}
        <div className="lp-price-card">
          <div className="lp-price-tier-badge lp-price-tier-free">LEARN</div>
          <div className="lp-price-name">Free</div>
          <div className="lp-price-amount">
            <span className="lp-price-dollar">$0</span>
            <span className="lp-price-period">/forever</span>
          </div>
          <p className="lp-price-tagline">Plan your wealth journey</p>
          <ul className="lp-price-features">
            <li><span className="lp-price-check">&#10003;</span> Net Worth Tracker</li>
            <li><span className="lp-price-check">&#10003;</span> Lifecycle Planner</li>
            <li><span className="lp-price-check">&#10003;</span> Wealth Playbook</li>
            <li><span className="lp-price-check">&#10003;</span> 4-Bucket System Guide</li>
            <li><span className="lp-price-check">&#10003;</span> Bucket Visualizer</li>
            <li><span className="lp-price-check">&#10003;</span> 1 industry watchlist</li>
            <li><span className="lp-price-check">&#10003;</span> Hourly email alerts</li>
          </ul>
          {!isAuthenticated ? (
            <Link to="/login" className="lp-btn lp-btn-outline lp-btn-lg" style={{ textAlign: 'center' }}>Start Free</Link>
          ) : (
            <Link to="/dashboard" className="lp-btn lp-btn-outline lp-btn-lg" style={{ textAlign: 'center' }}>Go to Dashboard</Link>
          )}
        </div>

        {/* Stock & Options Signals (Premier) */}
        <div className="lp-price-card lp-price-card-popular">
          <div className="lp-price-popular-tag">Start Here</div>
          <div className="lp-price-tier-badge lp-price-tier-premier">STOCK RESEARCH</div>
          <div className="lp-price-name">Stock & Options</div>
          <div className="lp-price-amount">
            <span className="lp-price-dollar">$29.99</span>
            <span className="lp-price-period">/mo</span>
          </div>
          <p className="lp-price-tagline">$29.99 for first 2 months, then $29.99/mo</p>
          <div className="lp-price-guarantee">60-day money-back guarantee</div>
          <ul className="lp-price-features">
            <li><span className="lp-price-check">&#10003;</span> Stock swing research</li>
            <li><span className="lp-price-check">&#10003;</span> Options research</li>
            <li><span className="lp-price-check">&#10003;</span> Daily stock briefing report</li>
            <li><span className="lp-price-check">&#10003;</span> 4 industry watchlists</li>
            <li><span className="lp-price-check">&#10003;</span> Custom watchlist (15 symbols)</li>
            <li><span className="lp-price-check">&#10003;</span> AI market insights</li>
            <li><span className="lp-price-check">&#10003;</span> Stock scanner</li>
          </ul>
          <Link to={isAuthenticated ? '/upgrade' : '/login?redirect=upgrade'} className="lp-btn lp-btn-primary lp-btn-lg" style={{ textAlign: 'center' }}>
            Start 60-Day Trial
          </Link>
        </div>

        {/* Algo Engine */}
        <div className="lp-price-card lp-price-card-pro">
          <div className="lp-price-tier-badge lp-price-tier-pro">ALGO ENGINE</div>
          <div className="lp-price-name">Algo Trading</div>
          <div className="lp-price-amount">
            <span className="lp-price-dollar-old">$59.99</span>
            <span className="lp-price-dollar">$49.99</span>
            <span className="lp-price-period">/mo per engine</span>
          </div>
          <p className="lp-price-tagline">for first year, then $59.99/mo</p>
          <div className="lp-price-requires">Requires Stock & Options</div>
          <ul className="lp-price-features">
            <li><span className="lp-price-check">&#10003;</span> Build your own strategy policies</li>
            <li><span className="lp-price-check">&#10003;</span> 4 strategy frameworks</li>
            <li><span className="lp-price-check">&#10003;</span> AI confidence scoring</li>
            <li><span className="lp-price-check">&#10003;</span> Trailing stop & profit protection</li>
            <li><span className="lp-price-check">&#10003;</span> Alpaca brokerage execution</li>
            <li><span className="lp-price-check">&#10003;</span> Portfolio analytics</li>
          </ul>
          <Link to={isAuthenticated ? '/upgrade' : '/login?redirect=upgrade'} className="lp-btn lp-btn-outline lp-btn-lg" style={{ textAlign: 'center' }}>
            Add Algo Trading
          </Link>
        </div>
      </div>

      {/* All Notches Bundle — above individual cards */}
      <div style={{
        maxWidth: '700px', margin: '2rem auto', padding: '1.5rem 2rem', textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(236,72,153,0.1), rgba(139,92,246,0.1))',
        border: '2px solid rgba(236,72,153,0.3)', borderRadius: '12px',
      }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: '#ec4899', marginBottom: '0.5rem' }}>BEST VALUE</div>
        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#ffffff', marginBottom: '0.25rem' }}>
          All Notches Bundle — <span style={{ color: '#ec4899' }}>$199/mo</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0.5rem 0 1rem', fontSize: '0.95rem' }}>
          SPX + SPY + ES + MES — save $57/mo vs subscribing individually ($256/mo)
        </p>
        <Link to={isAuthenticated ? '/upgrade' : '/login?redirect=upgrade'} className="lp-btn lp-btn-primary lp-btn-lg" style={{ textAlign: 'center' }}>
          Get the Bundle
        </Link>
      </div>

      {/* Individual Signal Products */}
      <p className="lp-section-label" style={{ marginTop: '1rem' }}>Or Subscribe Individually</p>
      <h2 className="lp-section-title" style={{ fontSize: '1.6rem' }}>Professional Quantitative Research</h2>
      <p className="lp-section-desc">
        Live option chain analysis, AI-ranked candidates, and full transparency reports. Not a chatbot — a quantitative engine on real market data.
      </p>

      <div className="lp-pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', maxWidth: '1000px', margin: '0 auto 2rem' }}>
        {signalProducts.map((p) => (
          <div key={p.name} className="lp-price-card" style={{ borderColor: `${p.color}40`, display: 'flex', flexDirection: 'column' }}>
            {p.badge && <div className="lp-price-popular-tag" style={{ backgroundColor: p.color }}>{p.badge}</div>}
            <div className="lp-price-name" style={{ fontSize: '1.1rem' }}>{p.name}</div>
            <div className="lp-price-amount" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
              <span className="lp-price-dollar" style={{ color: p.color }}>{p.price}</span>
              <span className="lp-price-period">/mo</span>
            </div>
            <p className="lp-price-tagline">{p.desc}</p>
            <ul className="lp-price-features" style={{ fontSize: '0.85rem', flex: 1 }}>
              <li><span className="lp-price-check" style={{ color: p.color }}>&#10003;</span> AI-ranked trade candidates</li>
              <li><span className="lp-price-check" style={{ color: p.color }}>&#10003;</span> Real-time IV & skew analysis</li>
              <li><span className="lp-price-check" style={{ color: p.color }}>&#10003;</span> Downloadable Word reports</li>
            </ul>
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Link to={isAuthenticated ? '/upgrade' : '/login?redirect=upgrade'} className="lp-btn lp-btn-outline lp-btn-lg" style={{ textAlign: 'center', borderColor: `${p.color}60`, color: p.color }}>
                Subscribe
              </Link>
              <Link to={(p as any).landingPage} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textDecoration: 'none' }}>
                Learn more &rarr;
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* How to Subscribe */}
      <div style={{ maxWidth: '700px', margin: '2.5rem auto 0', textAlign: 'center' }}>
        <h3 style={{ color: '#ffffff', fontSize: '1.3rem', marginBottom: '1.5rem' }}>How to Subscribe</h3>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
          {[
            { step: '1', title: 'Create Account', desc: 'Free signup takes 30 seconds' },
            { step: '2', title: 'Choose Products', desc: 'Pick the research you follow' },
            { step: '3', title: 'Pay with PayPal', desc: 'Secure checkout, cancel anytime' },
          ].map((s) => (
            <div key={s.step} style={{ flex: '1 1 180px', maxWidth: '200px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto 0.75rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                fontSize: '1.1rem', fontWeight: 700, color: '#fff',
              }}>
                {s.step}
              </div>
              <div style={{ color: '#ffffff', fontWeight: 600, marginBottom: '0.25rem' }}>{s.title}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>{s.desc}</div>
            </div>
          ))}
        </div>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', marginTop: '1.25rem' }}>
          Every paid product includes a 60-day money-back guarantee. Full refund within 30 days, no questions asked.
        </p>
      </div>
    </section>
  );
};

/* ---- Footer ---- */

/* ---- Research Section ----
 * Highlights our published research: Notches Scorecard + Weekly (public,
 * bridged from /n2), plus the quality studies we use to decide what to ship
 * (weight-fit A/B and the pick postmortem). Both pages public.
 */
const ResearchSection: React.FC = () => {
  const cards = [
    {
      to: '/n2/scorecard',
      eyebrow: 'PUBLIC SCORECARD · NOTCHES',
      eyebrowColor: '#d4a53a',
      title: 'Every research reading, scored publicly',
      subtitle: 'Four independent models (COT · GEX · IV · AVWAP) on 353 real readings across 3 years. Every reading is published as it fires and scored at the model\'s own horizon. Losers shown alongside winners.',
      stat: { value: '353', label: 'published readings' },
      cta: 'Open the scorecard →',
      external: false,
    },
    {
      to: '/n2/research',
      eyebrow: 'NOTCHES WEEKLY · FREE',
      eyebrowColor: '#d4a53a',
      title: 'Our weekly research letter',
      subtitle: 'A new letter every Sunday. Plain-English read on positioning, gamma, vol regime, and what our models are telling the desk this week. No paywall on the letter itself.',
      stat: { value: 'weekly', label: 'every Sunday' },
      cta: 'Read the archive →',
      external: false,
    },
    {
      to: '/markets/test-results',
      eyebrow: 'RESEARCH QUALITY STUDY',
      eyebrowColor: '#22c55e',
      title: 'The new ranker beat the old one by $121,970',
      subtitle: 'Across 118 pick-pairs on ES / SPX / CL. Every category improved or held even. Zero regressions.',
      stat: { value: '+$121,970', label: 'Δ over 118 pairs' },
      cta: 'Sign in to read the study →',
      external: false,
      gated: true,
    },
    {
      to: '/markets/postmortem',
      eyebrow: 'PICK POSTMORTEM',
      eyebrowColor: '#f59e0b',
      title: 'What our picks actually did in the market',
      subtitle: 'Every top-3 pick from a two-week window, walked hour-by-hour against the option chain we archive.',
      stat: { value: '94%', label: 'hit a green mark' },
      cta: 'Sign in to read the postmortem →',
      external: false,
      gated: true,
    },
  ];
  return (
    <section className="lp-section" style={{ maxWidth: 1000, margin: '0 auto' }}>
      <p className="lp-section-label">Research</p>
      <h2 className="lp-section-title" style={{ fontSize: '1.8rem' }}>How our research actually performs</h2>
      <p className="lp-section-desc" style={{ maxWidth: 620 }}>
        Free public research from our Notches program, plus the quality studies
        we use to decide what to ship. No hand-picked highlights &mdash; full pick sets
        and every reading, replayed against real chain history in plain English.
      </p>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1.25rem', marginTop: '1.5rem',
      }}>
        {cards.map(c => (
          <Link
            key={c.to} to={c.to}
            style={{
              textDecoration: 'none', color: 'inherit', display: 'block',
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${c.eyebrowColor}30`,
              borderRadius: 12, padding: '1.5rem',
              transition: 'transform 0.2s, border-color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = c.eyebrowColor; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = `${c.eyebrowColor}30`; }}
          >
            <div style={{
              fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.09em',
              color: c.eyebrowColor, marginBottom: '0.5rem',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span>{c.eyebrow}</span>
              {c.gated && (
                <span style={{
                  fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em',
                  color: '#94a3b8', padding: '2px 6px',
                  border: '1px solid #64748b55', borderRadius: 3,
                  textTransform: 'uppercase',
                }}>Sign-in required</span>
              )}
            </div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 0.5rem', color: '#f9fafb', lineHeight: 1.35 }}>
              {c.title}
            </h3>
            <p style={{ fontSize: '0.9rem', color: '#cbd5e1', lineHeight: 1.6, margin: '0 0 1rem' }}>
              {c.subtitle}
            </p>
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 10,
              padding: '0.75rem 1rem', borderRadius: 8,
              background: `${c.eyebrowColor}12`, border: `1px solid ${c.eyebrowColor}30`,
              marginBottom: '0.75rem',
            }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 700, color: c.eyebrowColor }}>{c.stat.value}</span>
              <span style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>{c.stat.label}</span>
            </div>
            <div style={{ color: c.eyebrowColor, fontWeight: 600, fontSize: '0.9rem' }}>{c.cta}</div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const FooterSection: React.FC = () => (
  <footer className="lp-footer">
    <div className="lp-footer-inner">
      <div className="lp-footer-brand">
        <div className="lp-nav-logo" style={{ width: 28, height: 28, fontSize: '0.75rem' }}>W</div>
        <span className="lp-footer-brand-name">Wealth Financial Systems</span>
      </div>
      <div className="lp-footer-cols">
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">Learn</div>
          <Link to="/learn/4-bucket-system" className="lp-footer-link">4-Bucket System</Link>
          <Link to="/playbook"              className="lp-footer-link">Wealth Playbook</Link>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">Research</div>
          <Link to="/spx"                  className="lp-footer-link">SPX Notches</Link>
          <Link to="/spy"                  className="lp-footer-link">SPY Notches</Link>
          <Link to="/es"                   className="lp-footer-link">ES Notches</Link>
          <Link to="/mes"                  className="lp-footer-link">MES Notches</Link>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">Tools</div>
          <Link to="/net-worth"            className="lp-footer-link">Net Worth Tracker</Link>
          <Link to="/fire-calculator"      className="lp-footer-link">FIRE Calculator</Link>
          <Link to="/bucket-visualizer"    className="lp-footer-link">Bucket Visualizer</Link>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">Account</div>
          <Link to="/login"                className="lp-footer-link">Login</Link>
          <Link to="/login"                className="lp-footer-link">Register</Link>
          <Link to="/upgrade"              className="lp-footer-link">Upgrade</Link>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">Company</div>
          <Link to="/contact"              className="lp-footer-link">Contact</Link>
          <Link to="/terms"                className="lp-footer-link">Terms</Link>
          <Link to="/privacy"              className="lp-footer-link">Privacy</Link>
        </div>
      </div>
      <div className="lp-footer-bottom">
        <p className="lp-footer-copy">&copy; {new Date().getFullYear()} Wealth Financial Systems. All rights reserved.</p>
        <p className="lp-footer-disclaimer">For educational purposes only. Not financial advice.</p>
      </div>
    </div>
  </footer>
);

/* ============================================
   Main Landing Page
   ============================================ */

export const LandingPage: React.FC = () => {
  return (
    <div className="lp-page">
      <LandingNav />
      <HeroSection />
      <AudienceSection />
      <div className="lp-divider" />
      <ResearchSection />
      <div className="lp-divider" />
      <FooterSection />
    </div>
  );
};
